import React, { useEffect, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  FormControl,
  TextField,
  Typography,
  useTheme,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  Stack,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Cookies from 'js-cookie';
import BackButton from '../backButton';
import { APIUpdateDigitalPass, APIGetOperator } from '../../utils/apiCalled';
import { usePrivilege } from '../../PrivilegeContext';

export default function PageDigitalPass({
  digitalPass,
  setOpen,
  setSuccMsg,
  setErrMsg,
  setVariant,
  setMainMsg,
  setModalTriggerAPICalled,
  handleCloseMorePasses,
}) {
  const { role } = usePrivilege();
  const [serviceName, setServiceName] = useState('');
  const [description, setDescription] = useState('');
  const [passType, setPassType] = useState('');
  const [price, setPrice] = useState('');
  const [percentageDiscount, setPercentageDiscount] = useState('');
  const [serviceProviders, setServiceProviders] = useState('');
  const [status, setStatus] = useState('');
  const [validityPeriodDays, setValidityPeriodDays] = useState('');
  const [isReturnTrip, setIsReturnTrip] = useState('');
  const [erlServiceType, setErlServiceType] = useState('');
  const [applicationLimits, setApplicationLimits] = useState({ TREK: '', ERL: '' });
  const [termCond, setTermCond] = useState([{ title: '', description: '' }]);
  const [zoneId, setZoneId] = useState([]);
  const [passengerLimits, setPassengerLimits] = useState({ adult: '', child: '', senior: '' });
  const [serviceCode, setServiceCode] = useState('');
  const [assignedOperators, setAssignedOperators] = useState([]);
  const [operatorsList, setOperatorsList] = useState([]);

  const [posLoading, setPosLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const theme = useTheme();

  useEffect(() => {
    if (digitalPass) {
      setServiceName(digitalPass.serviceName || '');
      setDescription(digitalPass.description || '');
      setPassType(digitalPass.passType || '');
      setPrice(digitalPass.price != null ? digitalPass.price.toString() : '');
      setPercentageDiscount(digitalPass.percentageDiscount != null ? digitalPass.percentageDiscount.toString() : '');
      setServiceProviders(digitalPass.serviceProviders || '');
      setStatus(digitalPass.status || 'ACTIVE');
      setValidityPeriodDays(digitalPass.validityPeriodDays != null ? digitalPass.validityPeriodDays.toString() : '');
      setIsReturnTrip(digitalPass.isReturnTrip ?? '');
      setErlServiceType(digitalPass.erlServiceType ?? '');
      setServiceCode(digitalPass.serviceCode || '');

      if (digitalPass.applicationLimits) {
        setApplicationLimits({
          TREK: digitalPass.applicationLimits.TREK != null ? digitalPass.applicationLimits.TREK.toString() : '',
          ERL: digitalPass.applicationLimits.ERL != null ? digitalPass.applicationLimits.ERL.toString() : '',
        });
      } else {
        setApplicationLimits({ TREK: '', ERL: '' });
      }

      if (Array.isArray(digitalPass.termCond) && digitalPass.termCond.length > 0) {
        setTermCond(digitalPass.termCond);
      } else {
        setTermCond([{ title: '', description: '' }]);
      }

      if (Array.isArray(digitalPass.zoneId) && digitalPass.zoneId.length > 0) {
        setZoneId(digitalPass.zoneId);
      } else {
        setZoneId([]);
      }

      if (digitalPass.passengerLimits) {
        setPassengerLimits({
          adult: digitalPass.passengerLimits.adult != null ? digitalPass.passengerLimits.adult.toString() : '',
          child: digitalPass.passengerLimits.child != null ? digitalPass.passengerLimits.child.toString() : '',
          senior: digitalPass.passengerLimits.senior != null ? digitalPass.passengerLimits.senior.toString() : '',
        });
      } else {
        setPassengerLimits({ adult: '', child: '', senior: '' });
      }

      // Set assignedOperators based on role
      if (role === 101 || role === 900) {
        // For super admin (101) or admin (900), use digitalPass's assignedOperators
        if (digitalPass.assignedOperators) {
          setAssignedOperators(Array.isArray(digitalPass.assignedOperators)
            ? digitalPass.assignedOperators
            : [digitalPass.assignedOperators]);
        } else {
          setAssignedOperators([]);
        }
      } else {
        // For non-super admin, use selectedOperatorId from cookies
        const operatorAssigned = Cookies.get('selectedOperatorId');
        if (operatorAssigned) {
          const assignedIds = operatorAssigned.split(',').map((id) => id.trim());
          setAssignedOperators(assignedIds);
        } else {
          setAssignedOperators([]);
        }
      }
    }
  }, [digitalPass, role]);

  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const response = await APIGetOperator(setLoading);
        if (response.status === 200) {
          if (role === 101 || role === 900) {
            setOperatorsList(response.data);
          } else {
            const operatorAssigned = Cookies.get('selectedOperatorId');
            const assignedIds = operatorAssigned
              ? operatorAssigned.split(',').map((id) => id.trim())
              : [];
            const matchedOperators = response.data.filter((op) => (
              assignedIds.includes(op._id.toString())
            ));
            setOperatorsList(matchedOperators);
          }
        }
      } catch (error) {
        // Error fetching operators - silently fail
      }
    };

    fetchOperators();
  }, [role]);

  // Prefill assigned operators with selectedOperatorId when
  // editing existing pass (only for non-super admins)
  useEffect(() => {
    if (digitalPass && operatorsList.length > 0 && role !== 101 && role !== 900) {
      const operatorAssigned = Cookies.get('selectedOperatorId');
      if (operatorAssigned && (!digitalPass.assignedOperators
        || digitalPass.assignedOperators.length === 0)) {
        const assignedIds = operatorAssigned.split(',').map((id) => id.trim());
        if (assignedIds.length > 0 && assignedOperators.length === 0) {
          setAssignedOperators(assignedIds);
        }
      }
    }
  }, [operatorsList, digitalPass, role, assignedOperators.length]);

  const fontHeaderStyle = {
    color: theme.palette.grey[700],
    fontWeight: 'fontWeightMedium',
    mr: 5,
    minWidth: '250px',
  };

  const formControlStyle = {
    mb: 4,
    display: 'flex',
    flexDirection: 'row',
  };

  const buttonStyle = {
    border: '1px solid',
    borderColor: 'grey.300',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    color: theme.palette.grey[700],
    backgroundColor: 'background.paper',
    '&:hover': {
      bgcolor: alpha(theme.palette.grey[500], 0.08),
    },
  };

  const confirmButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.primary.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.90),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.main,
      color: 'white',
      opacity: '0.6',
    },
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case 'serviceName':
        setServiceName(value);
        break;
      case 'description':
        setDescription(value);
        break;
      case 'passType':
        setPassType(value);
        if (value === 'UNLIMITED') {
          setApplicationLimits({ TREK: '', ERL: '' });
        }
        break;
      case 'price':
        setPrice(value);
        break;
      case 'percentageDiscount':
        setPercentageDiscount(value);
        break;
      case 'serviceProviders':
        setServiceProviders(value);
        break;
      case 'status':
        setStatus(value);
        break;
      case 'validityPeriodDays':
        setValidityPeriodDays(value);
        break;
      case 'isReturnTrip':
        setIsReturnTrip(value);
        break;
      case 'erlServiceType':
        setErlServiceType(value);
        break;
      default:
        break;
    }
  };

  const handleApplicationLimitChange = (key, value) => {
    setApplicationLimits((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleTermCondChange = (index, field, value) => {
    const newTermCond = [...termCond];
    newTermCond[index] = {
      ...newTermCond[index],
      [field]: value,
    };
    setTermCond(newTermCond);
  };

  const handleAddTermCond = () => {
    setTermCond([...termCond, { title: '', description: '' }]);
  };

  const handleRemoveTermCond = (index) => {
    if (termCond.length > 1) {
      const newTermCond = termCond.filter((_, i) => i !== index);
      setTermCond(newTermCond);
    }
  };

  const handleZoneIdChange = (event) => {
    const { value } = event.target;
    if (typeof value === 'string') {
      setZoneId(value.split(',').map((id) => id.trim()).filter((id) => id.length > 0));
    } else {
      setZoneId(value);
    }
  };

  const handlePassengerLimitChange = (key, value) => {
    setPassengerLimits((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const removeAllState = () => {
    setServiceName('');
    setDescription('');
    setPassType('');
    setPrice('');
    setPercentageDiscount('');
    setServiceProviders('');
    setStatus('');
    setValidityPeriodDays('');
    setIsReturnTrip('');
    setErlServiceType('');
    setApplicationLimits({ TREK: '', ERL: '' });
    setTermCond([{ title: '', description: '' }]);
    setZoneId([]);
    setPassengerLimits({ adult: '', child: '', senior: '' });
    setServiceCode('');
    setAssignedOperators([]);
  };

  const apiUpdateCalled = async (value, codeName) => {
    try {
      const result = await APIUpdateDigitalPass(setPosLoading, codeName, value);
      if (result.status === 200) {
        setPosLoading(false);
        removeAllState();
        setOpen(true);
        setVariant('success');
        setSuccMsg(`Digital Pass ${value.serviceName} has successfully been updated`);
        setMainMsg('Digital Pass Successfully Updated');
        setModalTriggerAPICalled(true);
      } else {
        setPosLoading(false);
        removeAllState();
        const errorResponse = result.response?.data;
        const errorMessage = errorResponse?.message || 'Failed to update digital pass';
        setOpen(true);
        setVariant('error');
        setErrMsg(errorMessage);
        setMainMsg('Digital Pass Update Unsuccessful');
        setModalTriggerAPICalled(true);
      }
    } catch (err) {
      setPosLoading(false);
      removeAllState();
      setOpen(true);
      setVariant('error');
      setErrMsg(`Error! ${err.message || 'Please try again'}`);
      setMainMsg('Digital Pass Update Unsuccessful');
    }
  };

  const handleSubmit = async () => {
    const validationError = (() => {
      if (!serviceName) return 'Service Name is required';
      if (!description) return 'Description is required';
      if (!passType) return 'Pass Type is required';
      if (!price || Number(price) <= 0) return 'Price must be greater than 0';
      if (!percentageDiscount
        || Number(percentageDiscount) < 1 || Number(percentageDiscount) > 100) {
        return 'Percentage Discount must be between 1 and 100';
      }
      if (!serviceProviders) return 'Service Provider is required';
      if (!isReturnTrip) return 'Return Trip option is required';
      if (!erlServiceType) return 'ERL Service Type is required';
      if (!termCond || termCond.length === 0) return 'At least one Term & Condition is required';
      if (termCond.some((tc) => !tc.title || !tc.description)) {
        return 'All Term & Conditions must have both title and description';
      }
      if (passType === 'LIMITED') {
        if (!applicationLimits.TREK && !applicationLimits.ERL) {
          return 'At least one Application Limit is required for LIMITED pass type';
        }
        if (applicationLimits.TREK && (Number(applicationLimits.TREK) <= 0
        || !Number.isInteger(Number(applicationLimits.TREK)))) {
          return 'TREK Application Limit must be a positive integer';
        }
        if (applicationLimits.ERL && (Number(applicationLimits.ERL) <= 0
        || !Number.isInteger(Number(applicationLimits.ERL)))) {
          return 'ERL Application Limit must be a positive integer';
        }
      }
      if (validityPeriodDays && (Number(validityPeriodDays) <= 0
      || !Number.isInteger(Number(validityPeriodDays)))) {
        return 'Validity Period Days must be a positive integer';
      }
      return null;
    })();

    if (validationError) {
      setOpen(true);
      setVariant('error');
      setErrMsg(validationError);
      setMainMsg('Validation Error');
      return;
    }

    const payload = {
      serviceName,
      description,
      passType,
      price: Number(price),
      percentageDiscount: Number(percentageDiscount),
      serviceProviders,
      isReturnTrip,
      erlServiceType,
      termCond: termCond.filter((tc) => tc.title && tc.description),
    };

    if (status) {
      payload.status = status;
    }

    if (validityPeriodDays) {
      payload.validityPeriodDays = Number(validityPeriodDays);
    }

    if (passType === 'LIMITED') {
      const limits = {};
      if (applicationLimits.TREK) {
        limits.TREK = Number(applicationLimits.TREK);
      }
      if (applicationLimits.ERL) {
        limits.ERL = Number(applicationLimits.ERL);
      }
      if (Object.keys(limits).length > 0) {
        payload.applicationLimits = limits;
      }
    }

    if (zoneId.length > 0) {
      payload.zoneId = zoneId;
    }

    const passengerLimitsObj = {};
    if (passengerLimits.adult) {
      passengerLimitsObj.adult = Number(passengerLimits.adult);
    }
    if (passengerLimits.child) {
      passengerLimitsObj.child = Number(passengerLimits.child);
    }
    if (passengerLimits.senior) {
      passengerLimitsObj.senior = Number(passengerLimits.senior);
    }
    if (Object.keys(passengerLimitsObj).length > 0) {
      payload.passengerLimits = passengerLimitsObj;
    }

    // Include assignedOperators (even if empty array to allow clearing)
    if (Array.isArray(assignedOperators)) {
      payload.assignedOperators = assignedOperators;
    }

    await apiUpdateCalled(payload, serviceCode);
    handleCloseMorePasses();
  };

  const isFormInvalid = () => {
    if (!serviceName || !description || !passType || !price || !percentageDiscount
      || !serviceProviders || !isReturnTrip || !erlServiceType) {
      return true;
    }

    if (Number(price) <= 0) return true;
    if (Number(percentageDiscount) < 1 || Number(percentageDiscount) > 100) return true;

    if (termCond.length === 0 || termCond.some((tc) => !tc.title || !tc.description)) {
      return true;
    }

    if (passType === 'LIMITED') {
      if (!applicationLimits.TREK && !applicationLimits.ERL) {
        return true;
      }
      if (applicationLimits.TREK && (Number(applicationLimits.TREK) <= 0
      || !Number.isInteger(Number(applicationLimits.TREK)))) {
        return true;
      }
      if (applicationLimits.ERL && (Number(applicationLimits.ERL) <= 0
      || !Number.isInteger(Number(applicationLimits.ERL)))) {
        return true;
      }
    }

    if (validityPeriodDays && (Number(validityPeriodDays) <= 0
    || !Number.isInteger(Number(validityPeriodDays)))) {
      return true;
    }

    // Check if unchanged
    if (digitalPass) {
      const isUnchanged = serviceName === digitalPass.serviceName
        && description === digitalPass.description
        && passType === digitalPass.passType
        && Number(price) === digitalPass.price
        && Number(percentageDiscount) === digitalPass.percentageDiscount
        && serviceProviders === digitalPass.serviceProviders
        && (status || 'ACTIVE') === (digitalPass.status || 'ACTIVE')
        && (validityPeriodDays || '') === (digitalPass.validityPeriodDays?.toString() || '')
        && isReturnTrip === digitalPass.isReturnTrip
        && erlServiceType === digitalPass.erlServiceType
        && JSON.stringify(applicationLimits) === JSON.stringify({
          TREK: digitalPass.applicationLimits?.TREK?.toString() || '',
          ERL: digitalPass.applicationLimits?.ERL?.toString() || '',
        })
        && JSON.stringify(termCond) === JSON.stringify(digitalPass.termCond || [])
        && JSON.stringify(zoneId) === JSON.stringify(digitalPass.zoneId || [])
        && JSON.stringify(passengerLimits) === JSON.stringify({
          adult: digitalPass.passengerLimits?.adult?.toString() || '',
          child: digitalPass.passengerLimits?.child?.toString() || '',
          senior: digitalPass.passengerLimits?.senior?.toString() || '',
        })
        && JSON.stringify(assignedOperators) === JSON.stringify(digitalPass.assignedOperators
          || []);

      if (isUnchanged) return true;
    }

    return false;
  };

  return (
    <>
      <Box sx={{ mt: 2 }} />
      <BackButton back={handleCloseMorePasses} type="passes" />
      <Box sx={{
        mt: 2, pb: 4, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
      }}
      >
        <Box>
          <Typography variant="h4">Edit Digital Pass</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'right' }}>
          <Button sx={buttonStyle} onClick={() => handleCloseMorePasses()}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Cancel
            </Typography>
          </Button>
          <Button sx={confirmButtonStyle} disabled={isFormInvalid()} onClick={handleSubmit}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Confirm
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box sx={{
        padding: 3, borderTop: '1px solid', borderColor: theme.palette.grey[300], display: 'flex',
      }}
      >
        <Box sx={{ width: '70%', mt: 2, pr: 4 }}>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Service Code
            </Typography>
            <TextField
              label="Service Code"
              name="serviceCode"
              value={serviceCode}
              disabled
              sx={{ flexGrow: 1 }}
            />
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Service Name
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Service Name"
              name="serviceName"
              value={serviceName}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
            />
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Description
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Description"
              name="description"
              value={description}
              onChange={handleInputChange}
              multiline
              rows={3}
              sx={{ flexGrow: 1 }}
            />
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Pass Type
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Pass Type</InputLabel>
              <Select
                label="Pass Type"
                name="passType"
                value={passType}
                onChange={handleInputChange}
                sx={{ flexGrow: 1 }}
              >
                <MenuItem value="LIMITED">Limited</MenuItem>
                <MenuItem value="UNLIMITED">Unlimited</MenuItem>
              </Select>
            </FormControl>
          </FormControl>

          {passType === 'LIMITED' && (
            <>
              <FormControl sx={formControlStyle}>
                <Typography sx={fontHeaderStyle}>
                  TREK Application Limit
                  {' '}
                  <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  label="TREK Limit"
                  type="number"
                  value={applicationLimits.TREK}
                  onChange={(e) => handleApplicationLimitChange('TREK', e.target.value)}
                  sx={{ flexGrow: 1 }}
                  helperText="Positive integer"
                />
              </FormControl>

              <FormControl sx={formControlStyle}>
                <Typography sx={fontHeaderStyle}>
                  ERL Application Limit
                  {' '}
                  <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  label="ERL Limit"
                  type="number"
                  value={applicationLimits.ERL}
                  onChange={(e) => handleApplicationLimitChange('ERL', e.target.value)}
                  sx={{ flexGrow: 1 }}
                  helperText="Positive integer"
                />
              </FormControl>
            </>
          )}

          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Price (MYR)
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Price"
              type="number"
              name="price"
              value={price}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Percentage Discount
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Percentage Discount"
              type="number"
              name="percentageDiscount"
              value={percentageDiscount}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
              inputProps={{ min: 1, max: 100 }}
              helperText="Between 1 and 100"
            />
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Service Provider
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Service Provider</InputLabel>
              <Select
                label="Service Provider"
                name="serviceProviders"
                value={serviceProviders}
                onChange={handleInputChange}
                sx={{ flexGrow: 1 }}
              >
                <MenuItem value="ERL">ERL</MenuItem>
                <MenuItem value="TREK">TREK</MenuItem>
              </Select>
            </FormControl>
          </FormControl>

          <Box sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Assign Operator(s)
            </Typography>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                id="assign-operators"
                options={operatorsList}
                disableCloseOnSelect
                disabled={role !== 101 && role !== 900}
                getOptionLabel={(option) => option.operatorName}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                value={
                  Array.isArray(assignedOperators)
                    ? operatorsList.filter((op) => assignedOperators.includes(op._id))
                    : []
                }
                onChange={(event, newValue) => {
                  const newOperatorIds = newValue.map((op) => op._id);
                  setAssignedOperators(newOperatorIds);
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      sx={{ mr: 1 }}
                      checked={selected}
                    />
                    <Typography variant="body2">{option.operatorName}</Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned Operators"
                    placeholder="Select operators"
                    variant="outlined"
                    fullWidth
                  />
                )}
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper',
                    borderRadius: 1,
                  },
                  '& .MuiChip-root': {
                    bgcolor: 'primary.light',
                    color: 'white',
                    fontWeight: 500,
                  },
                }}
              />
            </FormControl>
          </Box>

          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Status
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                name="status"
                value={status}
                onChange={handleInputChange}
                sx={{ flexGrow: 1 }}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Validity Period (Days)
            </Typography>
            <TextField
              label="Validity Period Days"
              type="number"
              name="validityPeriodDays"
              value={validityPeriodDays}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
              inputProps={{ min: 1 }}
              helperText="Optional - Positive integer"
            />
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Return Trip
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Return Trip</InputLabel>
              <Select
                label="Return Trip"
                name="isReturnTrip"
                value={isReturnTrip || ''}
                onChange={handleInputChange}
                sx={{ flexGrow: 1 }}
              >
                <MenuItem value="TRUE">True</MenuItem>
                <MenuItem value="FALSE">False</MenuItem>
              </Select>
            </FormControl>
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              ERL Service Type
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>ERL Service Type</InputLabel>
              <Select
                label="ERL Service Type"
                name="erlServiceType"
                value={erlServiceType || ''}
                onChange={handleInputChange}
                sx={{ flexGrow: 1 }}
              >
                <MenuItem value="TRANSIT">Transit</MenuItem>
                <MenuItem value="EXPRESS">Express</MenuItem>
              </Select>
            </FormControl>
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Zone IDs
            </Typography>
            <TextField
              label="Zone IDs (comma-separated)"
              name="zoneId"
              value={zoneId.join(', ')}
              onChange={handleZoneIdChange}
              sx={{ flexGrow: 1 }}
              helperText="Optional - Comma-separated zone IDs"
            />
          </FormControl>

          <Box sx={{ ...formControlStyle, flexDirection: 'column', mb: 4 }}>
            <Typography sx={fontHeaderStyle}>
              Terms & Conditions
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Stack spacing={3} sx={{ width: '100%', mt: 3 }}>
              {termCond.map((term, index) => (
                <Box
                  key={term.title || term.description || `term-${index}`}
                  sx={{
                    display: 'flex', gap: 2, alignItems: 'flex-start', pb: 1,
                  }}
                >
                  <TextField
                    label="Title"
                    value={term.title}
                    onChange={(e) => handleTermCondChange(index, 'title', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Description"
                    value={term.description}
                    onChange={(e) => handleTermCondChange(index, 'description', e.target.value)}
                    multiline
                    rows={2}
                    sx={{ flex: 2 }}
                  />
                  <IconButton
                    onClick={() => handleRemoveTermCond(index)}
                    disabled={termCond.length === 1}
                    color="error"
                    sx={{ mt: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddTermCond}
                variant="outlined"
                sx={{ alignSelf: 'flex-start', mt: 1 }}
              >
                Add Term & Condition
              </Button>
            </Stack>
          </Box>

          <Box sx={{ ...formControlStyle, flexDirection: 'column', mb: 4 }}>
            <Typography sx={fontHeaderStyle}>
              Passenger Limits (Optional)
            </Typography>
            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <TextField
                label="Adult"
                type="number"
                value={passengerLimits.adult}
                onChange={(e) => handlePassengerLimitChange('adult', e.target.value)}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Child"
                type="number"
                value={passengerLimits.child}
                onChange={(e) => handlePassengerLimitChange('child', e.target.value)}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Senior"
                type="number"
                value={passengerLimits.senior}
                onChange={(e) => handlePassengerLimitChange('senior', e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Stack>
          </Box>
        </Box>
      </Box>
    </>
  );
}
